import { ColorNeutralDarkGrey, Spacing16, Spacing24, Spacing72 } from 'autogen/design-tokens/tokens';
import type { VariantPickerModel } from 'autogen/swagger/all';
import { MoreDetails } from 'autogen/translation-keys/trans-website-shared';
import imageOverlayStyle from 'components/shared/style/imageOverlay';
import { customScrollbar } from 'modules/helpers/mixins';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { breakpoint, sizes, zIndex } from 'variables';
import Loader from 'components/Loader/Loader';
import Image from 'components/generic/Image';
import { GetVariantColorPickerForPdpQueryKey, GetVariantPickerForPlpQueryKey } from 'autogen/swagger/all/hooks';
import { useIsFetching } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import VerticalSelection from './components/VerticalSelection';
import HorizontalSelection from './components/HorizontalSelection';
import { containerMargin } from './components/shared/styles';
import SelectionCallbackContext from './shared/context/selectionCallback';
import Footer from './components/Footer';
import TrackingParamsContext from './shared/context/TrackingParams';
import ActiveSectionContext from './shared/context/activeSection';
import { createVariantPickerStore, VariantPickerContext } from './shared/store';
import InlineLinkActionButton from 'components/generic/InlineButton/InlineLinkActionButton';

// #region Styles
const Wrapper = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1;
    max-width: 100%;
`;

const ScrollContainer = styled.div`
    flex: 0 1 100%;
    overflow: auto;
    -webkit-overflow-scrolling: touch;

    ${customScrollbar}
`;

const TopContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    padding-top: ${Spacing72};
    padding-bottom: ${Spacing16};
    gap: ${Spacing24};

    &:after {
        content: '';

        ${imageOverlayStyle}
    }
`;

const ProductImage = styled(Image.WithScaling)`
    align-self: center;
    height: 136px;
    object-fit: contain;
    aspect-ratio: 1;

    ${breakpoint.up(sizes.lg)} {
        height: 206px;
    }
`;

const TopLinkStyling = css`
    align-self: end;
    color: ${ColorNeutralDarkGrey};

    ${containerMargin}
`;

const StyledInlineButton = styled(InlineLinkActionButton)`
    ${TopLinkStyling}
`;

const SpinnerOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    z-index: ${zIndex.backdrop};
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.3);

    display: flex;
    justify-content: center;
    align-items: center;
`;

// #endregion Styles

function checkForMoreChoices(variantPickerModel?: VariantPickerModel, activeSectionIndex?: number): boolean {
    if (variantPickerModel && activeSectionIndex !== undefined) {
        const { sections } = variantPickerModel;

        return sections[activeSectionIndex + 1]?.variants?.length > 1;
    }

    return false;
}

function getSelectedVariantById(activeSectionIndex: number, variantPickerModel?: VariantPickerModel) {
    if (!variantPickerModel) return undefined;

    return variantPickerModel.sections[activeSectionIndex].variants.find(
        (item) => item.productId === variantPickerModel.productId
    );
}

export default function VariantPicker({
    model,
    isPDPRequest = false,
    ...restProps
}: {
    model: VariantPickerModel;
    isPDPRequest?: boolean;
}) {
    const [isLoading, setIsLoading] = useState(false);
    const variantPickerStore = useRef(createVariantPickerStore({ model }));
    const { productId, image, layoutType, price, sections, linkAction, action } =
        variantPickerStore.current.getState().model;

    const { t } = useTranslation();
    const isPdpLoading = useIsFetching([GetVariantColorPickerForPdpQueryKey]);
    const isPlpLoading = useIsFetching([GetVariantPickerForPlpQueryKey]);

    const [activeSectionIndex, setActiveSectionIndex] = useState(0);
    const [hasMoreChoices, setHasMoreChoices] = useState(checkForMoreChoices(model, activeSectionIndex));
    const activeSplash = getSelectedVariantById(activeSectionIndex, model)?.splash;
    const [totalSectionAmount, setTotalSectionAmount] = useState<number | null>(model?.sections.length || 1);
    const [debouncedRequest] = useDebouncedCallback(() => {
        setIsLoading(!!isPdpLoading || !!isPlpLoading);
    }, 150);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        debouncedRequest();
    }, [isPdpLoading, isPlpLoading, debouncedRequest]);

    useEffect(() => {
        if (model) {
            setHasMoreChoices(checkForMoreChoices(model, activeSectionIndex));
        }
    }, [model, activeSectionIndex]);

    useEffect(() => {
        if (model) {
            const { layoutType } = model;
            setTotalSectionAmount(layoutType === 'Horizontal' ? null : model.sections.length);
        }
    }, [model]);

    return (
        <VariantPickerContext.Provider value={variantPickerStore.current}>
            <ActiveSectionContext.Provider value={{ activeSectionIndex, totalSectionAmount, setActiveSectionIndex }}>
                <Wrapper {...restProps}>
                    <ScrollContainer>
                        <TopContainer>
                            <ProductImage
                                src={image}
                                sizes={[[sizes.xl, 206], 136]}
                                transformType="WidthScale"
                                imageType="product"
                                alt=""
                            />

                            {!isPDPRequest && (
                                <StyledInlineButton action={linkAction}>{t(MoreDetails)}</StyledInlineButton>
                            )}
                        </TopContainer>

                        <div>
                            <SelectionCallbackContext model={model} isPDPRequest={isPDPRequest}>
                                {layoutType === 'Horizontal' ? (
                                    <HorizontalSelection sections={sections} price={price} />
                                ) : (
                                    <VerticalSelection sections={sections} price={price} />
                                )}
                            </SelectionCallbackContext>
                        </div>
                    </ScrollContainer>

                    <TrackingParamsContext.Provider
                        value={activeSplash ? [{ param_name: 'splash_type', param_value: activeSplash.type }] : null}
                    >
                        <Footer
                            action={action}
                            productId={productId}
                            linkAction={linkAction}
                            isPDPRequest={isPDPRequest}
                            hasMoreChoices={hasMoreChoices}
                        />
                    </TrackingParamsContext.Provider>

                    {isLoading && (
                        <SpinnerOverlay>
                            <Loader />
                        </SpinnerOverlay>
                    )}
                </Wrapper>
            </ActiveSectionContext.Provider>
        </VariantPickerContext.Provider>
    );
}
